"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { GroqImage } from "@/helpers/Image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import type { PageAffiliateOldDesignSchema } from "@/sanity/pages/PageAffiliateOldDesignTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { resolveAffiliateRules } from "@/lib/affiliateGlobalSections";

export default function Rules({ data }: { data: PageAffiliateOldDesignSchema }) {
  const rules = resolveAffiliateRules(data);

  return (
    <section
      id="rules"
      className="w-full h-full lg:px-0 py-[8rem] px-[2rem] lg:py-[15rem]"
    >
      <div className="relative mx-auto w-full">
        <div className="relative mx-auto w-full z-[1] flex flex-col lg:items-center">
          {rules.chip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <ChipSerif text={rules.chip} />
            </motion.div>
          )}
          <motion.div
            className="richtext richtext-start lg:richtext-center prose-p:text-[2rem] lg:prose-p:text-[2rem] flex flex-col gap-[1.5rem]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {rules.text && (
              <PortableText value={rules.text} components={richTextComponent} />
            )}
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-[1rem] mt-[3rem] lg:mt-[4rem] w-[min(160.5rem,100%)]">
            {rules.items.map((item, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.25, delay: index * 0.08, ease: [0.22, 0.61, 0.36, 1] }}
              >
                <div
                  className="flex items-center h-full border gap-[2.75rem] rounded-[2rem] bg-[#FCFDFD] p-[1rem] border-black/[0.02] lg:p-[2rem]"
                  style={{ boxShadow: "0 -1.257px 5.027px 0 rgba(0, 0, 0, 0.10) inset" }}
                >
                  {item.icon && (
                    <GroqImage
                      src={item.icon}
                      alt="icon"
                      className="w-[7.25rem] h-[7.25rem] min-w-[7.25rem] min-h-[7.25rem]"
                    />
                  )}
                  <div>
                    <p className="text-[#6F6F6F] text-[2rem] lg:text-[2rem] font-[500] leading-[140%] mb-0">
                      {item.text}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
